import React, { useEffect, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useParams, useNavigate } from "react-router-dom";
import { fetchPlayerInfo } from "../../actions/staff/clubPlayerActions";
import WeeklyChart from "../common/WeeklyChart";
import NotificationMessage from "../../components/notification/NotificationMessage";
import ThemeContext from "../../theme/ThemeContext";
import TaskOverview from '../../components/taskoverview/TaskOverview';
import { useTranslation } from 'react-i18next';
import { formatDate, formatBirth } from '../../components/dateUtils';
import asset from '../../img/asset.png';
import { Container } from "react-bootstrap";
import Button from "../../components/cofButton/Button";
import './PlayerScreen.css';

const PlayerHomeScreen = () => {
  const { playerId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  console.log(userInfo);

  const { loading, error, playerInfo } = useSelector(
    (state) => state.playerInfo
  );

  console.log(playerInfo)

  const theme = useContext(ThemeContext);
  const { colors, fonts } = theme;

  // State za upravljanje brojem prikazanih notifikacija
  const [visibleNotifications, setVisibleNotifications] = useState(5);


  useEffect(() => {
    dispatch(fetchPlayerInfo(userInfo.club, playerId));
    // Reset visible notifications when player changes
    setVisibleNotifications(5);
  }, [dispatch, playerId]);

  // Generisanje datuma za poslednjih sedam dana (hronološki - od najstarijih ka najnovijim)
  const currentDate = new Date();
  const lastSevenDays = [...Array(7).keys()].map((days) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() - (6 - days)); // Počinjemo od pre 6 dana
    return date.toISOString().slice(0, 10); // Formatiranje u "YYYY-MM-DD" formatu
  });

  // Generisanje podataka za grafikone - direktno mapiranje
  const physicalData = playerInfo
    ? lastSevenDays.map((date, index) => ({
      date,
      rating: playerInfo.weeklyPhysicalHistory[index],
    }))
    : [];

  const mentalData = playerInfo
    ? lastSevenDays.map((date, index) => ({
      date,
      rating: playerInfo.weeklyMentalHistory[index],
    }))
    : [];

  const handleTaskClick = (taskId) => {
    // Navigacija na željenu rutu
    navigate(`/club/${userInfo.club}/scheduledtask/${taskId}`);
  };

  const loadMoreNotifications = () => {
    setVisibleNotifications(prev => prev + 5);
  };
  
  const calculateSleepDuration = (bedTimeStr, wakeTimeStr) => {
    if (bedTimeStr === "not filled" || wakeTimeStr === "not filled") return "not filled";

    const bedTime = new Date(bedTimeStr);
    let wakeTime = new Date(wakeTimeStr);

    // Adjust wake time if it is earlier than bed time (assume it's the next day)
    if (wakeTime <= bedTime) {
      wakeTime.setDate(wakeTime.getDate() + 1);
    }

    const durationMs = wakeTime - bedTime;
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${durationHours} hours and ${durationMinutes} minutes`;
  };

  const bedTimeStr = playerInfo && playerInfo.dailyStatusesWeeklyOverview && playerInfo.dailyStatusesWeeklyOverview.length > 0
    ? playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].bedTime
    : "not filled";

  const wakeTimeStr = playerInfo && playerInfo.dailyStatusesWeeklyOverview && playerInfo.dailyStatusesWeeklyOverview.length > 0
    ? playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].wakeTime
    : "not filled";

  return (
    <div className="player-screen">
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Error: {error}</p>
        </div>
      ) : (
        playerInfo && (
          <div className="player-content">
            {/* Hero Section */}
            <div className="player-hero">
              <div className="hero-content">
                <h1 className="hero-title">
                  {playerInfo.name} {playerInfo.surname}
                </h1>
                <div className="hero-subtitle">
                  #{playerInfo.jersynum} • {playerInfo.position}
                </div>
              </div>
              <div className="hero-background"></div>
            </div>

            {/* Player Information Cards */}
            <div className="player-info-section">
              <h2 className="section-title">
                {t("Player Information").toUpperCase()}
              </h2>
              
              <div className="info-grid">
                {/* Personal Info Card */}
                <div className="info-card">
                  <h3 className="card-title">👤 Personal Information</h3>
                  <div className="card-content">
                    <div className="info-row">
                      <span className="info-label">{t("Name")}:</span>
                      <span className="info-value">{playerInfo.name}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Surname")}:</span>
                      <span className="info-value">{playerInfo.surname}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Age")}:</span>
                      <span className="info-value">
                        {playerInfo.age && playerInfo.age !== "not filled" ? formatBirth(playerInfo.age) : "N/A"}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Nationality")}:</span>
                      <span className="info-value">{playerInfo.nationality}</span>
                    </div>
                  </div>
                </div>

                {/* Player Details Card */}
                <div className="info-card">
                  <h3 className="card-title">⚽ Player Details</h3>
                  <div className="card-content">
                    <div className="info-row">
                      <span className="info-label">{t("Position")}:</span>
                      <span className="info-value">{playerInfo.position}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Jersey number")}:</span>
                      <span className="info-value">{playerInfo.jersynum}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Passport expiration date")}:</span>
                      <span className="info-value">
                        {playerInfo.passportExpirationDate && playerInfo.passportExpirationDate !== "not filled"
                          ? formatBirth(playerInfo.passportExpirationDate)
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Current Status Card */}
                <div className="info-card">
                  <h3 className="card-title">📊 Current Status</h3>
                  <div className="card-content">
                    <div className="info-row">
                      <span className="info-label">{t("Fitnessstatus")}:</span>
                      <span className="info-value">
                        {playerInfo?.dailyStatusesWeeklyOverview?.length > 0
                          ? playerInfo.dailyStatusesWeeklyOverview[
                              playerInfo.dailyStatusesWeeklyOverview.length - 1
                            ].physicalStatus
                          : "not filled"}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("Mentalstatus")}:</span>
                      <span className="info-value">
                        {playerInfo?.dailyStatusesWeeklyOverview?.length > 0
                          ? playerInfo.dailyStatusesWeeklyOverview[
                              playerInfo.dailyStatusesWeeklyOverview.length - 1
                            ].mentalStatus
                          : "not filled"}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("SleepTime")}:</span>
                      <span className="info-value">{calculateSleepDuration(bedTimeStr, wakeTimeStr)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t("SleepQuality")}:</span>
                      <span className="info-value">
                        {playerInfo?.dailyStatusesWeeklyOverview?.length > 0
                          ? playerInfo.dailyStatusesWeeklyOverview[
                              playerInfo.dailyStatusesWeeklyOverview.length - 1
                            ].sleepStatus
                          : "not filled"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Descriptions */}
            <div className="status-descriptions">
              <div className="status-card">
                <h3 className="status-title">
                  💪 {t("Informacije o fizickom statusu:").toUpperCase()}
                </h3>
                <div className="status-description">
                  {playerInfo.dailyStatusesWeeklyOverview &&
                    playerInfo.dailyStatusesWeeklyOverview.length > 0 &&
                    playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].physicalStatusDescription
                    ? `"${playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].physicalStatusDescription}"`
                    : t("Nema opisa.")}
                </div>
              </div>

              <div className="status-card">
                <h3 className="status-title">
                  🧠 {t("Informacije o mentalnom statusu:").toUpperCase()}
                </h3>
                <div className="status-description">
                  {playerInfo.dailyStatusesWeeklyOverview &&
                    playerInfo.dailyStatusesWeeklyOverview.length > 0 &&
                    playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].mentalStatusDescription
                    ? `"${playerInfo.dailyStatusesWeeklyOverview[playerInfo.dailyStatusesWeeklyOverview.length - 1].mentalStatusDescription}"`
                    : t("Nema opisa.")}
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="charts-section">
              <div className="chart-container">
                <h3 className="chart-title">
                  📈 {t("Physical State")}
                </h3>
                <div className="chart-average">
                  <span className="average-label">{t("Average")}:</span>
                  <span className="average-value">
                    {playerInfo.averagePhysicalRating ? playerInfo.averagePhysicalRating.toFixed(1) : "0.0"}
                  </span>
                </div>
                <div className="chart-wrapper">
                  <WeeklyChart data={physicalData} type="physical" />
                </div>
              </div>

              <div className="chart-container">
                <h3 className="chart-title">
                  🧠 {t("Mental Condition")}
                </h3>
                <div className="chart-average">
                  <span className="average-label">{t("Average")}:</span>
                  <span className="average-value">
                    {playerInfo.averageMentalRating ? playerInfo.averageMentalRating.toFixed(1) : "0.0"}
                  </span>
                </div>
                <div className="chart-wrapper">
                  <WeeklyChart data={mentalData} type="mental" />
                </div>
              </div>
            </div>

            {/* Notifications and Tasks Wrapper */}
            <div className="notifications-and-tasks-wrapper">
              {/* Notifications Section */}
              <div className="notifications-section">
                <h3 className="section-title">
                  🔔 {t("Notifications")}
                </h3>
                <div className="notifications-container">
                  {playerInfo.notifications && playerInfo.notifications.notifications && playerInfo.notifications.notifications.length > 0 ? (
                    <>
                      {playerInfo.notifications.notifications.slice(0, visibleNotifications).map((notification, index) => (
                        <div key={index} style={{ 
                          maxWidth: '320px', 
                          margin: '12px auto',
                          width: '100%'
                        }}>
                          <NotificationMessage
                            assigned={notification.send_to.name}
                            time={formatDate(notification.createdAt)}
                            notification={notification.text}
                          />
                        </div>
                      ))}
                      
                      {/* Load more dugme */}
                      {visibleNotifications < playerInfo.notifications.notifications.length && (
                        <div className="load-more-container">
                          <Button
                            onClick={loadMoreNotifications}
                            style={{
                              backgroundColor: colors.primaryColor,
                              color: "white",
                              border: "none",
                              padding: "10px 20px",
                              cursor: "pointer",
                              marginTop: "15px"
                            }}
                          >
                            {t("Read More")}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ color: '#ffffff', textAlign: 'center', fontStyle: 'italic' }}>
                      {t("No Notifications Available")}
                    </p>
                  )}
                </div>
              </div>

              {/* Assigned Tasks Section */}
              <div className="tasks-section">
                <h3 className="section-title">
                  📋 {t("Assigned Tasks")}
                </h3>
                <div className="tasks-container">
                  {playerInfo.assignedTasks.map((assignedTask) => (
                    <TaskOverview
                      key={assignedTask._id}
                      task={assignedTask.name}
                      description={assignedTask.description}
                      time={formatDate(assignedTask.startTime)}
                      location={assignedTask.location}
                      scheduledTaskId={assignedTask._id}
                      onTaskClick={handleTaskClick}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PlayerHomeScreen;
